interface StrategyValues {
  industryId?: string
  subIndustryIds?: string[]
  groupIds?: string[]
  businessId?: string
}

type StrategyType = 'INDUSTRY_SEARCH' | 'GROUP_SEARCH' | 'BUSINESS_TARGET_LIST'

export interface CampaignEmailEventItem {
  id: string
  eventType: string
  occurredAt: string | Date
}

export interface CampaignRecipientActivity {
  id: string
  contactName: string
  email: string
  recipientStatus: string
  emailJobStatus: string | null
  delivered: boolean
  opened: boolean
  clicked: boolean
  bounced: boolean
  failed: boolean
  openEventCount: number
  clickEventCount: number
  latestEventType: string | null
  latestEventAt: string | Date | null
  eventCount: number
  events: CampaignEmailEventItem[]
}

export interface CampaignActivityFeedItem extends CampaignEmailEventItem {
  recipientId: string
  contactName: string
  email: string
}

export interface CampaignEmailMetrics {
  campaignType: string
  hasExternalAnalytics: boolean
  recipientCount: number
  metrics: {
    delivered: number
    opened: number
    clicked: number
    bounced: number
    unsubscribed: number
    failed: number
    pending: number
    totalEvents: number
  }
  eventTotals: {
    sent: number
    delivered: number
    opened: number
    clicked: number
    bounced: number
    unsubscribed: number
    failed: number
    processed: number
    deferred: number
  }
  rates: {
    deliveryRate: number
    openRate: number
    clickRate: number
  }
  recipients: CampaignRecipientActivity[]
  activityFeed: CampaignActivityFeedItem[]
}

export const useCampaigns = () => {
  const createCampaign = async (campaignData: any) => {
    const response = await $fetch('/api/v1/campaigns', {
      method: 'POST',
      body: campaignData,
    }) as any

    return response
  }

  const getCampaigns = async (query?: {
    campaignType?: string
    sourceBusinessId?: string
  }): Promise<any> => {
    const response = await $fetch(`/api/v1/campaigns`, {
      method: 'GET',
      query,
    }) as any

    return response
  }

  const getCampaign = async (campaignId: string): Promise<any> => {
    const response = await $fetch(`/api/v1/campaigns/${campaignId}`, {
      method: 'GET',
    }) as any

    return response
  }

  const updateCampaign = async (campaignId: string, campaignData: any): Promise<any> => {
    const response = await $fetch(`/api/v1/campaigns/${campaignId}`, {
      method: 'PUT',
      body: campaignData,
    }) as any

    return response
  }

  const sendTestEmail = async (campaignId: string, email: string, subject: string, htmlContent: string): Promise<{ success: boolean, message: string, data: any }> => {
    const response = await $fetch(`/api/v1/campaigns/send-test`, {
      method: 'POST',
      body: { campaignId, email, subject, htmlContent },
    }) as any

    return response
  }

  const getContactsByStrategy = async (strategyType: StrategyType, values: StrategyValues): Promise<{ success: boolean, data: any[], count: number }> => {
    const response = await $fetch(`/api/v1/campaign/get-contacts`, {
      method: 'POST',
      body: {
        strategyType,
        values,
      },
    }) as any

    return response
  }

  /**
   * Schedule a campaign to be sent at a specific date/time.
   * This will:
   * 1. Create the campaign in the external mailer
   * 2. Ensure all recipients exist in the mailer (create if needed)
   * 3. Add recipients to the external campaign
   * 4. Update local campaign status to SCHEDULED
   * 
   * @param campaignId - The ID of the campaign to schedule
   * @param scheduledAt - ISO datetime string (must be at least 2 hours from now, max 4 weeks)
   * @returns Promise with success status and updated campaign data
   */
  const scheduleCampaign = async (campaignId: string, scheduledAt: string): Promise<{ success: boolean, message: string, data: any }> => {
    const response = await $fetch(`/api/v1/campaigns/schedule`, {
      method: 'POST',
      body: {
        campaignId,
        scheduledAt,
      },
    }) as any

    return response
  }

  const sendCampaign = async (campaignId: string): Promise<{ success: boolean, message: string, data: any }> => {
    const response = await $fetch(`/api/v1/campaigns/${campaignId}/send`, {
      method: 'POST',
    }) as any

    return response
  }

  const getCampaignAnalytics = async (campaignId: string): Promise<{ success: boolean, message: string, data: any }> => {
    const response = await $fetch(`/api/v1/campaigns/${campaignId}/analytics`, {
      method: 'GET',
    }) as any

    return response
  }

  const getCampaignEmailMetrics = async (
    campaignId: string,
  ): Promise<{ success: boolean, data: CampaignEmailMetrics }> => {
    const response = await $fetch(`/api/v1/campaigns/${campaignId}/email-metrics`, {
      method: 'GET',
    }) as { success: boolean, data: CampaignEmailMetrics }

    return response
  }

  return {
    createCampaign,
    getCampaigns,
    getCampaign,
    updateCampaign,
    sendTestEmail,
    getContactsByStrategy,
    scheduleCampaign,
    sendCampaign,
    getCampaignAnalytics,
    getCampaignEmailMetrics,
  }
}
